import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  E2EBadge,
  IdLabel,
  KiTranslatedBadge,
  ReadinessChip,
  StateChip,
} from '../../components/atoms.js';
import { type PanelField, PanelFieldInput } from '../../components/panel-fields.js';
import { SavedHint } from '../../components/saved-hint.js';
import { ApiError } from '../../lib/api.js';
import {
  type TestCaseDetail as TestCaseDetailType,
  type TestCaseOrigin,
  catalogApi,
} from '../../lib/catalog-api.js';
import { invalidateCatalogTestCase } from '../../lib/catalog-cache.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { simplificationsApi } from '../../lib/simplifications-api.js';
import { sourceLabel } from '../../lib/source-label.js';
import { toast } from '../../lib/toast.js';
import { useSavedFlash } from '../../lib/use-saved-flash.js';
import { useShowSourceRef } from '../../lib/use-source-ref.js';
import { translationFillFailureOf } from '../../lib/use-translation-fill-run.js';
import { StepTable } from './step-table.js';
import { type TestCaseDraft, testCaseFields, toTestCaseDraft } from './testcase-fields.js';

// Design-Spec 3.5 / Screen 3a Detailspalte: Breadcrumb (KAT-2: Hierarchie neben, nie in der
// ID) · Kopfkarte (ID Mono orange + Chips BEREIT/E2E/Positiv + Default-Tester) · Titel/
// Vorbedingung/Erwartet DE/EN nebeneinander, jede Seite mit KiTranslatedBadge solange ihr
// jeweiliges *_de_ai/*_en_ai-Flag steht (LNG-3, Task 10 / #63/#64: der Import kann in beide
// Richtungen laufen). Der Badge-Zustand kommt IMMER vom Server (Query-Invalidierung nach dem
// Save) — nie lokal simulieren: eine manuelle DE- ODER EN-Änderung kann das Flag auf dem
// Server löschen, das UI liest nur nach, was zurückkommt.

// Welches Feld traegt welches KI-Uebersetzt-Flag (LNG-3). Felder ohne Eintrag tragen keins.
// Task 10 (#63/#64): der Import kann in beide Richtungen laufen (DE- oder EN-Quelle) — deshalb
// hat jede Sprachseite ihr eigenes Flag, das Badge sitzt jeweils auf der KI-gefuellten Seite.
const AI_FLAG_BY_FIELD: Record<string, keyof TestCaseDetailType | undefined> = {
  title_de: 'title_de_ai',
  title_en: 'title_en_ai',
  precondition_de: 'precondition_de_ai',
  precondition_en: 'precondition_en_ai',
  expected_de: 'expected_de_ai',
  expected_en: 'expected_en_ai',
};

// Ein Feld = ein PATCH-Aufruf mit genau diesem Feld, damit der Server pro Sprache entscheiden
// kann, ob das jeweilige *_en_ai-Flag stehen bleibt oder fällt. Ein Voll-PATCH wuerde alle
// Abzeichen gleichzeitig loeschen.
function EditableField({
  field,
  draft,
  badge,
  canEdit,
  saved,
  onSave,
}: {
  field: PanelField<TestCaseDraft>;
  draft: TestCaseDraft;
  badge?: boolean;
  canEdit: boolean;
  // #96: true fuer die paar Sekunden nach einem erfolgreichen Save GENAU dieses Feldes — steuert
  // den ruhigen, feldnahen Hinweis (Task 9-Muster, kein Toast pro Feld).
  saved: boolean;
  onSave: (patch: Partial<TestCaseDraft>) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(draft);

  // Uebernimmt Server-Werte, solange nicht gerade editiert wird (unveraendertes Verhalten).
  useEffect(() => {
    if (!editing) setLocal(draft);
  }, [draft, editing]);

  // #58: Ohne catalog.edit ist das Feld statischer Text — kein Klick-zum-Bearbeiten, kein Editor,
  // kein Speichern. Der Wert (und das KI-Übersetzt-Abzeichen) bleibt lesbar.
  if (!canEdit) {
    return (
      <div className="block w-full space-y-1 p-1 text-left">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">
          {field.labelKey}
          {badge && <KiTranslatedBadge />}
        </span>
        <span className="block whitespace-pre-wrap text-sm text-text-body">
          {renderValue(field, draft) || <span className="text-text-muted">—</span>}
        </span>
      </div>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="block w-full space-y-1 rounded-btn p-1 text-left hover:bg-[var(--bg-subtle)]"
      >
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">
          {field.labelKey}
          {badge && <KiTranslatedBadge />}
          <SavedHint visible={saved} />
        </span>
        <span className="block whitespace-pre-wrap text-sm text-text-body">
          {renderValue(field, draft) || <span className="text-text-muted">—</span>}
        </span>
      </button>
    );
  }

  return (
    <div className="space-y-1">
      <span className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
        {field.labelKey}
      </span>
      <PanelFieldInput field={field} draft={local} onChange={setLocal} autoFocus />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            // Nur das eine Feld schicken — der Server entscheidet daran, ob das *_en_ai-Flag
            // stehen bleibt oder faellt.
            const key = field.key as keyof TestCaseDraft;
            if (local[key] !== draft[key]) onSave({ [key]: local[key] } as Partial<TestCaseDraft>);
          }}
          className="rounded-btn bg-brand-accent px-2 py-1 text-xs text-white"
        >
          {t('catalog.actions.save')}
        </button>
        <button
          type="button"
          onClick={() => {
            setLocal(draft);
            setEditing(false);
          }}
          className="px-2 py-1 text-xs text-text-muted"
        >
          {t('catalog.actions.cancel')}
        </button>
      </div>
    </div>
  );
}

// Lesedarstellung: Auswahlfelder zeigen ihre Beschriftung, nicht den technischen Wert.
function renderValue(field: PanelField<TestCaseDraft>, draft: TestCaseDraft): string {
  if (field.kind === 'select') {
    const v = field.get(draft);
    return field.options.find((o) => o.value === v)?.label ?? '';
  }
  if (field.kind === 'checkbox') return String(field.get(draft));
  return field.get(draft);
}

// #113 Task 11 (A7): Die Herkunftszeile — „Teilprojekt · Modul · Prozess · E2E-Kette (Schritt N
// von M)". Sie beantwortet am Testfall zwei Fragen, die er selbst nicht beantwortet: wo steht er
// im Katalog, und in welcher Kette bildet er welchen Schritt.
//
// Drei Entscheidungen, die nicht aus der Anforderung folgen:
//
//  1. ALLE Ketten, nicht eine. Ein Testfall kann in mehreren Ketten liegen (A6 — der Kern dieses
//     Features), und das Testfall-Detail hat, anders als „Meine Tests", KEINEN Kettenkontext, aus
//     dem sich „die richtige" ableiten liesse. Eine willkuerlich gewaehlte Kette waere hier eine
//     falsche Aussage („Schritt 2 von 4" — in welcher Kette?). Reihenfolge: `human_id`
//     aufsteigend, vom Server vorgegeben; dieselbe stabile Ordnung wie im E2E-Reiter.
//
//  2. Verlinkt wird NUR auf Ziele, die es wirklich gibt:
//       Teilprojekt → /assignment?view=teilprojekt (die einzige Ansicht, die Teilprojekte mit
//                     ihren Prozessen zeigt) — und die ist in der Shell nur mit `catalog.edit`
//                     als Route registriert. Ohne das Recht bleibt es reiner Text, sonst landete
//                     der Klick wortlos auf dem Dashboard. Muster: Defect-Chip in playlist.tsx,
//                     `deeplinkForNotification`.
//       Prozess     → der Katalogbaum, auf diesen Prozess gefiltert (`?q=`). Einen Deeplink, der
//                     einen Prozess AUSWAEHLT, gibt es heute nicht (in der URL steht nur
//                     `?test_case=`); `?q=` ist der vorhandene Weg dorthin. `test_case` bleibt in
//                     der URL, damit der gerade gelesene Testfall offen bleibt.
//       Kette       → der E2E-Reiter, auf die Kettennummer gefiltert (`?q=`, e2e-tab.tsx sucht
//                     ueber human_id/name_de/name_en).
//     Das Modul bekommt bewusst KEINEN Link: fuer `modules` existiert im ganzen Frontend keine
//     Ansicht (die Tabelle entsteht heute nur im Seed). Ein Link ins Leere waere schlechter als
//     Text.
//
//  3. Sprachabhaengig: `lang === 'en' ? name_en || name_de : name_de || name_en`,
//     `lang = i18n.resolvedLanguage ?? 'de'`. Review-Befund Important 1 (Task 11): vorher stand
//     hier durchgaengig `name_de || name_en`, unabhaengig vom Sprachumschalter — waehrend die
//     Kettenleiste (chain-bar.tsx) fuer DIESELBE Kette schon sprachabhaengig entscheidet. Fuer
//     eine englischsprachige Nutzerin hiess dieselbe Kette in „Meine Tests" englisch, am
//     Testfall-Detail aber weiter deutsch. Jetzt dieselbe Regel wie chain-bar.tsx.
function OriginLine({
  origin,
  testCaseId,
  canOpenAssignment,
}: {
  // Optional, nicht weil der Server es weglassen darf, sondern weil waehrend eines Rollouts eine
  // aeltere Antwort ohne das Feld im react-query-Cache liegen kann. Seit Review-Befund Minor 6
  // deckt sich das mit dem Typ: `TestCaseDetail.origin` (catalog-api.ts) ist jetzt `origin?:`.
  origin: TestCaseOrigin | undefined;
  testCaseId: string;
  canOpenAssignment: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (!origin) return null;

  // Review-Befund Important 1 (Task 11): dieselbe Sprachregel wie die Kettenleiste (chain-bar.tsx)
  // fuer dieselbe Kette — `resolvedLanguage` statt `language`, weil das die tatsaechlich
  // AUFGELOESTE Sprache ist (i18next-Fallback-Kette bereits angewandt).
  const lang = i18n.resolvedLanguage ?? 'de';
  const linkClass = 'text-brand-accent hover:underline';
  const parts: { key: string; node: ReactNode }[] = [];

  if (origin.teilprojekt) {
    parts.push({
      key: 'teilprojekt',
      node: canOpenAssignment ? (
        <Link to="/assignment?view=teilprojekt" className={linkClass}>
          {origin.teilprojekt.name}
        </Link>
      ) : (
        <span>{origin.teilprojekt.name}</span>
      ),
    });
  }

  // A7: „Modul nur, wenn gesetzt" — `processes.module_id` ist nullbar, und heute traegt es kaum
  // ein Prozess. Kein Platzhalter, kein „—": das Glied faellt ersatzlos weg.
  if (origin.modul) {
    const modulName = pickLang(lang, origin.modul.name_de, origin.modul.name_en);
    parts.push({ key: 'modul', node: <span>{modulName}</span> });
  }

  if (origin.prozess) {
    const name = pickLang(lang, origin.prozess.name_de, origin.prozess.name_en);
    parts.push({
      key: 'prozess',
      node: (
        <Link
          to={`/catalog?tab=processes&q=${encodeURIComponent(name)}&test_case=${encodeURIComponent(testCaseId)}`}
          className={linkClass}
        >
          {name}
        </Link>
      ),
    });
  }

  for (const chain of origin.chains) {
    const chainName = pickLang(lang, chain.name_de, chain.name_en);
    parts.push({
      key: `chain-${chain.e2e_id}`,
      node: (
        <span>
          <Link
            to={`/catalog?tab=e2e&q=${encodeURIComponent(chain.human_id)}`}
            className={linkClass}
          >
            {chain.human_id} {chainName}
          </Link>{' '}
          ({t('catalog.origin.step', { step: chain.step, total: chain.total })})
        </span>
      ),
    });
  }

  // Ein Testfall ohne Prozess und ohne Kette hat keine Herkunft — dann steht hier nichts, statt
  // einer leeren Zeile mit Trennzeichen.
  if (parts.length === 0) return null;

  return (
    <nav
      aria-label={t('catalog.origin.label')}
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-text-muted"
    >
      {parts.map((part, i) => (
        <Fragment key={part.key}>
          {i > 0 && <span aria-hidden="true">·</span>}
          {part.node}
        </Fragment>
      ))}
    </nav>
  );
}

// Design-Spec 3.5: „Simplifications" als eigene Zelle der Kopfkarte — verknüpfte Simplifications
// als Chips + „Verknüpfen"-Button, der einen Checkbox-Picker gegen die volle Projekt-Liste
// öffnet. Die Link-API (`getLinks`/`setLinks`) ist je Simplification adressiert, nicht je
// Testfall — ein Reverse-Index (welche Simplifications verlinken DIESEN Testfall) existiert
// serverseitig nicht. Deshalb wird er hier clientseitig aus einem `getLinks`-Aufruf pro
// Simplification im Projekt gebildet (O(n) bei n = Anzahl Simplifications). Für V1-Katalog-
// größen unkritisch; dokumentierter Punkt statt Overengineering eines Reverse-Index.
function SimplificationsSection({
  projectId,
  testCaseId,
  canEdit,
}: {
  projectId: string;
  testCaseId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);

  const simplificationsQuery = useQuery({
    queryKey: ['simplifications', projectId],
    queryFn: () => simplificationsApi.list(projectId),
  });
  const sims = simplificationsQuery.data ?? [];
  const simIds = sims.map((s) => s.simplification_id).join(',');

  const linksQuery = useQuery({
    queryKey: ['simplification-links-for-tc', projectId, testCaseId, simIds],
    queryFn: async () => {
      const pairs = await Promise.all(
        sims.map(
          async (s) =>
            [
              s.simplification_id,
              await simplificationsApi.getLinks(projectId, s.simplification_id),
            ] as const,
        ),
      );
      return new Set(pairs.filter(([, ids]) => ids.includes(testCaseId)).map(([id]) => id));
    },
    enabled: sims.length > 0,
  });
  const linkedIds = linksQuery.data ?? new Set<string>();
  const linkedSims = sims.filter((s) => linkedIds.has(s.simplification_id));

  const toggleLink = useMutation({
    mutationFn: async ({
      simplificationId,
      linked,
    }: {
      simplificationId: string;
      linked: boolean;
    }) => {
      const current = await simplificationsApi.getLinks(projectId, simplificationId);
      const next = linked
        ? Array.from(new Set([...current, testCaseId]))
        : current.filter((id) => id !== testCaseId);
      return simplificationsApi.setLinks(projectId, simplificationId, next);
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['simplification-links-for-tc', projectId, testCaseId],
      }),
  });

  return (
    <div className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
          {t('simpl.linkedSimplifications')}
        </span>
        {/* #58: „Verknüpfen" (öffnet den Checkbox-Picker → setLinks) nur mit catalog.edit. Die
            verknüpften Simplifications (Chips) darunter bleiben für alle sichtbar. */}
        {canEdit && (
          <button
            type="button"
            onClick={() => setPickerOpen((open) => !open)}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10"
          >
            {t('simpl.link')}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {linkedSims.length === 0 && <span className="text-sm text-text-muted">—</span>}
        {linkedSims.map((s) => (
          <span
            key={s.simplification_id}
            className="rounded-chip border border-[var(--border)] bg-[var(--bg-subtle)] px-2 py-0.5 text-xs text-text-body"
          >
            {s.title}
          </span>
        ))}
      </div>

      {canEdit && pickerOpen && (
        <div className="max-h-48 space-y-1 overflow-y-auto rounded-btn border border-[var(--border)] p-2">
          {sims.length === 0 && <div className="text-xs text-text-muted">{t('catalog.empty')}</div>}
          {sims.map((s) => (
            <label
              key={s.simplification_id}
              className="flex items-center gap-2 text-sm text-text-body"
            >
              <input
                type="checkbox"
                checked={linkedIds.has(s.simplification_id)}
                onChange={(e) =>
                  toggleLink.mutate({
                    simplificationId: s.simplification_id,
                    linked: e.target.checked,
                  })
                }
              />
              {s.title}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function TestCaseDetail({
  projectId,
  testCaseId,
  onDeleted,
}: {
  projectId: string;
  testCaseId: string;
  // Auswahl im Katalog zurücksetzen, nachdem der Testfall gelöscht wurde (Panel schließen).
  onDeleted: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // #58: Testfall-Felder bearbeiten und Simplifications verknüpfen ist `catalog.edit` (Backend
  // erzwingt es zusätzlich). Ohne das Recht bleibt die Detailansicht rein lesend.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  // #112 (A6): derselbe Admin-Schalter, der die Quell-ID auch im Katalogbaum steuert.
  const showSourceRef = useShowSourceRef(projectId);
  // Löschen (inline-Bestätigung, Muster wie process-panel.tsx). deleteError trägt den 409-Fall
  // testcase_has_evidence sprechend, statt still auf errors.generic zu fallen.
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: tc, isLoading } = useQuery({
    queryKey: ['tc', projectId, testCaseId],
    queryFn: () => catalogApi.getTestCase(projectId, testCaseId),
  });

  // Die Prozessliste ist die AUSWAHL für die Zuordnung — sie wird deshalb nur noch mit
  // `catalog.edit` geladen. Der Anzeige-Zweck (Prozessname als Breadcrumb) ist mit Task 11 an die
  // Herkunftszeile übergegangen, die ihre Namen fertig vom Server bekommt (`tc.origin`); ein
  // Betrachter braucht die volle Liste dafür nicht mehr.
  const processesQuery = useQuery({
    queryKey: ['catalog-processes', projectId],
    queryFn: () => catalogApi.listProcesses(projectId),
    enabled: canEdit,
  });

  // Fuer die Standard-Tester-Auswahl der Feldliste (dieselbe Quelle wie das Anlege-Panel).
  const membersQuery = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => plansApi.members(projectId),
  });

  const invalidate = () => {
    invalidateCatalogTestCase(queryClient, projectId, testCaseId, tc?.process_id);
  };

  // #111: Fehlende Übersetzungen per KI ergänzen. Fehler zeigt der Handler selbst (errorToast:
  // false) — der globale Toast kennt weder das Ratenlimit noch den Teilerfolg.
  const fillTranslations = useMutation({
    mutationFn: () => catalogApi.fillTranslations(projectId, testCaseId, 'single'),
    meta: { errorToast: false },
    onSuccess: (res) => {
      // Auch bei Teilerfolg wurden Felder geschrieben → Caches immer neu laden.
      invalidate();
      if (res.failure) {
        // 200 mit Fehlerklasse = Teilerfolg (oder nichts geschrieben): kein grüner Erfolg.
        toast.error(
          t(res.filled > 0 ? 'catalog.translationFill.partial' : 'catalog.translationFill.failed'),
        );
        return;
      }
      if (res.filled === 0) {
        // Lücke besteht trotz Erfolg weiter: die KI hat den Text unverändert zurückgegeben (Eigenname).
        toast.info(
          t(
            res.remaining_gap
              ? 'catalog.translationFill.unchanged'
              : 'catalog.translationFill.nothing',
          ),
        );
        return;
      }
      toast.success(
        t(res.review ? 'catalog.translationFill.doneReview' : 'catalog.translationFill.done', {
          count: res.filled,
        }),
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t('catalog.translationFill.rateLimited'));
      } else if (err instanceof ApiError && err.status === 503) {
        // 503 = dauerhaft; der Körper nennt den Grund (Kostenbudget, Konfigurationsfehler, kein Gateway).
        const failure = translationFillFailureOf(err);
        toast.error(
          t(
            failure === 'cost_budget'
              ? 'catalog.translationFill.costBudget'
              : failure === 'structural'
                ? 'catalog.translationFill.failed'
                : 'catalog.translationFill.notConfigured',
          ),
        );
      } else if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        toast.error(mapApiError(t, err));
      } else {
        toast.error(t('catalog.translationFill.failed'));
      }
    },
  });

  const updateField = useMutation({
    mutationFn: (patch: Partial<TestCaseDraft>) =>
      catalogApi.updateTestCase(projectId, testCaseId, patch),
    onSuccess: invalidate,
  });
  // #96: ruhiges, feldnahes "gespeichert"-Feedback (Muster aus Task 9, scope-table.tsx) — Flash-ID
  // ist der Feldname, damit nur das gerade gespeicherte Feld seinen Hinweis zeigt.
  const { savedId, flash } = useSavedFlash();
  const saveField = (field: string, patch: Partial<TestCaseDraft>) => {
    updateField.mutate(patch, { onSuccess: () => flash(field) });
  };

  // Eigene Mutation statt `updateField`: die Prozess-Zuordnung gehört nicht zur Feldliste
  // (TestCaseDraft) — sie sitzt in der Kopfzeile und schreibt eine Beziehung, kein Feld.
  const updateProcess = useMutation({
    mutationFn: (processId: string | null) =>
      catalogApi.updateTestCase(projectId, testCaseId, { process_id: processId }),
    onSuccess: () => {
      // Der Testfall wechselt den Ast: alte wie neue Prozessliste und die „Ohne Prozess"-Gruppe
      // zeigen sonst weiter den alten Stand — deshalb ohne processId (alle Prozess-Listen).
      invalidateCatalogTestCase(queryClient, projectId, testCaseId);
      flash('process_id');
    },
  });

  const remove = useMutation({
    mutationFn: () => catalogApi.deleteTestCase(projectId, testCaseId),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: () => {
      // Den gelöschten Detail-Eintrag ENTFERNEN statt zu invalidieren: ein aktiver Beobachter holte ihn
      // sonst neu und bekäme ein 404. Zuerst entfernen, dann invalidieren — der Detail-Key ist dann
      // nicht mehr im Cache und die Invalidierung trifft nur Baum, Prozess-Listen, „Ohne Prozess",
      // gefilterte Volliste und Zuordnungsseite, die den Testfall sonst weiter zeigen.
      queryClient.removeQueries({ queryKey: ['tc', projectId, testCaseId] });
      invalidateCatalogTestCase(queryClient, projectId, testCaseId);
      onDeleted();
    },
  });

  if (isLoading) {
    return <div className="p-6 text-text-muted">{t('catalog.loading')}</div>;
  }
  // #36/#42: Ein veralteter/ungültiger `?test_case=`-Deeplink (der Testfall existiert nicht mehr)
  // zeigte sonst DAUERHAFT „Lädt…", weil der fehlgeschlagene Fetch `!tc` wahr lässt. Jetzt ein
  // ehrlicher „nicht gefunden"-Zustand statt eines endlosen Spinners.
  if (!tc) {
    return <div className="p-6 text-text-muted">{t('catalog.detail.notFound')}</div>;
  }

  const draft = toTestCaseDraft(tc);

  return (
    <div className="space-y-4 p-6">
      {/* #113 Task 11 (A7): Die Herkunftszeile steht ganz oben — sie ist der Breadcrumb (KAT-2:
          Hierarchie NEBEN der ID, nie in ihr) und ersetzt die frühere Nur-Lese-Prozesszeile, die
          dasselbe nur unvollständig sagte. Für Bearbeiter bleibt die Prozess-Auswahl darunter
          stehen: das ist ein Bedienelement, kein Breadcrumb. */}
      <OriginLine origin={tc.origin} testCaseId={testCaseId} canOpenAssignment={canEdit} />

      {canEdit && processesQuery.isSuccess && processesQuery.data.length === 0 ? (
        // Kein Prozess vorhanden: eine Auswahl, in der nur „Ohne Prozess" steht, sieht aus wie ein
        // Fehler. Stattdessen der Weg, der weiterhilft — angelegt wird ein Prozess im Baum links.
        <div className="text-xs text-status-warn">{t('catalog.detail.noProcessesYet')}</div>
      ) : canEdit ? (
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <label htmlFor="tc-process">{t('catalog.fields.process')}</label>
          <select
            id="tc-process"
            aria-label={t('catalog.fields.process')}
            value={tc.process_id ?? ''}
            onChange={(e) => updateProcess.mutate(e.target.value || null)}
            className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
          >
            {/* Leerwert bleibt wählbar: die Zuordnung ist zurücknehmbar, und ein importierter
                Testfall startet genau hier. */}
            <option value="">{t('catalog.fields.processNone')}</option>
            {(processesQuery.data ?? []).map((p) => (
              <option key={p.process_id} value={p.process_id}>
                {pickLang(lang, p.name_de, p.name_en)}
              </option>
            ))}
          </select>
          <SavedHint visible={savedId === 'process_id'} />
        </div>
      ) : null}

      <div className="space-y-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* #112 (A6): Quell-ID aus der Importdatei neben der TestFlow-ID. Nur lesbar — bewusst
              kein Bedienelement: sie ist der Anker in die Quelldatei, Korrekturweg ist der
              Neuimport. Der Admin-Schalter `show_source_ref` blendet sie aus. */}
          <IdLabel
            id={tc.human_id}
            secondary={showSourceRef ? sourceLabel(tc.source_ref, tc.origin?.chains ?? []) : null}
          />
          <ReadinessChip value={tc.readiness} />
          {tc.e2e_memberships.length > 0 && <E2EBadge />}
          {tc.polarity && (
            <StateChip
              label={t(`catalog.polarity.${tc.polarity}`)}
              tone={tc.polarity === 'positiv' ? 'ok' : 'warn'}
            />
          )}
          {tc.translation_review && (
            <StateChip label={t('catalog.translationReview')} tone="warn" />
          )}
          {/* #111: nur bei echter Lücke und mit Bearbeitungsrecht (Backend prüft beides erneut).
              Der Lauf kann einige Sekunden dauern → gesperrter Knopf mit Ladehinweis. */}
          {canEdit && tc.translation_gap && (
            <button
              type="button"
              onClick={() => fillTranslations.mutate()}
              disabled={fillTranslations.isPending}
              className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-text-primary hover:border-brand-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              {fillTranslations.isPending
                ? t('catalog.translationFill.running')
                : t('catalog.translationFill.action')}
            </button>
          )}
          {tc.default_tester_id && (
            <span className="text-xs text-text-muted">
              {t('catalog.fields.defaultTester')}:{' '}
              {tc.default_tester_name ?? t('plans.unknownTester')}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {testCaseFields(t, membersQuery.data ?? []).map((field) => {
            const aiFlagKey = AI_FLAG_BY_FIELD[field.key];
            return (
              <EditableField
                key={field.key}
                field={field}
                draft={draft}
                badge={aiFlagKey ? (tc[aiFlagKey] as boolean) : false}
                canEdit={canEdit}
                saved={savedId === field.key}
                onSave={(patch) => saveField(field.key, patch)}
              />
            );
          })}
        </div>
      </div>

      <SimplificationsSection projectId={projectId} testCaseId={testCaseId} canEdit={canEdit} />

      <StepTable projectId={projectId} testCaseId={testCaseId} steps={tc.steps} />

      {/* Löschen — der einzige Katalog-Entitätstyp, dem der Knopf bisher fehlte (Backend-Route,
          API-Client und Bestätigungstext existierten schon). Muster: process-panel.tsx. */}
      {canEdit && (
        <div className="flex flex-col items-end gap-1 border-t border-[var(--border)] pt-3">
          {confirming ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-text-body">{t('catalog.confirm.deleteTestCase')}</span>
              <button
                type="button"
                onClick={async () => {
                  setDeleteError(null);
                  try {
                    await remove.mutateAsync();
                  } catch (err) {
                    setDeleteError(mapApiError(t, err));
                    setConfirming(false);
                  }
                }}
                disabled={remove.isPending}
                className="rounded-btn bg-status-fail px-3 py-1 text-xs text-white disabled:opacity-60"
              >
                {t('catalog.actions.confirmDelete')}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="px-2 py-1 text-xs text-text-muted"
              >
                {t('catalog.actions.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setConfirming(true);
              }}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-status-fail"
            >
              {t('catalog.actions.delete')}
            </button>
          )}
          {deleteError && <div className="text-xs text-status-fail">{deleteError}</div>}
        </div>
      )}
    </div>
  );
}
